/**
 * Google Analytics 4 wrapper + custom event tracking.
 *
 * - Loads gtag.js once on first call (idempotent), only when REACT_APP_GA_MEASUREMENT_ID is set
 * - Sends NO PII (no email, name, IBAN). Only user role + opaque hashed user id
 * - Respects offline / blocked-script environments by failing silently
 * - All event names follow GA4 recommended conventions (snake_case)
 *
 * Public API:
 *   initAnalytics()                                — call once at app startup
 *   trackPageView(path)                            — SPA route changes
 *   trackEvent(name, params)                       — generic
 *   trackRegistration(method)                      — successful sign-up
 *   trackLogin(method)                             — successful login
 *   trackKYCStarted() / trackKYCSubmitted() / trackKYCApproved()
 *   trackTicketPaymentIntent(network, currency)    — Buy Ticket clicked
 *   trackTicketPurchased(amount, currency, drawId) — payment confirmed
 *   trackReferralShare(channel)                    — share button clicked
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
let _initDone = false;

function _gtag(...args) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

export function initAnalytics() {
  if (_initDone || !GA_ID || typeof document === 'undefined') return;
  _initDone = true;

  // Inject gtag.js asynchronously
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  script.onerror = () => {
    // Ad-blocker or offline — non-fatal
    _initDone = false;
  };
  document.head.appendChild(script);

  // Bootstrap dataLayer (gtag.js looks for window.gtag function arguments converted to dataLayer)
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    send_page_view: true,
    anonymize_ip: true,
  });
}

export function trackPageView(path) {
  if (!GA_ID || typeof window === 'undefined') return;
  _gtag('event', 'page_view', {
    page_path: path || window.location.pathname,
    page_title: document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!GA_ID) return;
  _gtag('event', name, params);
}

export function trackRegistration(method = 'email') {
  trackEvent('sign_up', { method });
}

export function trackLogin(method = 'email') {
  trackEvent('login', { method });
}

export function trackKYCStarted() {
  trackEvent('kyc_started');
}

export function trackKYCSubmitted() {
  trackEvent('kyc_submitted');
}

export function trackKYCApproved() {
  trackEvent('kyc_approved');
}

export function trackTicketPaymentIntent(network, currency) {
  trackEvent('begin_checkout', {
    currency: currency || 'EUR',
    items: [{ item_name: 'lottery_ticket', item_category: 'draw_1' }],
    payment_network: network || 'unknown',
  });
}

export function trackTicketPurchased(amount, currency, drawId) {
  trackEvent('purchase', {
    transaction_id: `draw-${drawId}-${Date.now()}`,
    value: amount || 150,
    currency: currency || 'EUR',
    items: [{ item_name: 'lottery_ticket', item_category: `draw_${drawId}`, price: amount || 150, quantity: 1 }],
  });
}

export function trackReferralShare(channel = 'native_share') {
  trackEvent('share', { method: channel, content_type: 'ticket' });
}

export function trackError(label, fatal = false) {
  trackEvent('exception', { description: label, fatal });
}

const analytics = {
  init: initAnalytics,
  trackPageView,
  trackEvent,
  trackRegistration,
  trackLogin,
  trackKYCStarted,
  trackKYCSubmitted,
  trackKYCApproved,
  trackTicketPaymentIntent,
  trackTicketPurchased,
  trackReferralShare,
  trackError,
};

export default analytics;
